import { Asset } from '../../../../../../../types/apiTypes';
import {
  formatNumberCommas,
  formatNumberCommasWithDollar,
} from '../../../../../../../utils/CurrencyUtils';
import { useScreenView } from '../../../../../../../utils/ScreenViewContext';
import { capitalize } from '../../../../../../../utils/capitalize';
import { FLEET_POLICY_LABEL } from '../fleet-insurance/fleetInsurance';
import { useFleetInsurance } from '../fleet-insurance/useFleetInsurance';

interface AssetOverviewProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
  selectedAsset: any | null;
  setSelectedAsset: (asset: Asset) => void;
  assetImageUrl: string;
  setAssetImageUrl: (url: string) => void;
}

function AssetOverview({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
  selectedAsset,
  setSelectedAsset,
  assetImageUrl,
  setAssetImageUrl,
}: AssetOverviewProps) {
  const { screenView } = useScreenView();
  // THT-675 — a fleet-wide policy makes the per-asset amount meaningless (the
  // asset stores 0), so the row states the policy instead of showing "$0.00".
  const { isFleetMode: isFleetInsurance } = useFleetInsurance();
  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <div
            className={`flex h-full w-full flex-col items-center gap-6 border-[1px] border-bordergray bg-white p-4 align-middle`}
          >
            <div className="flex h-[50%] w-full flex-row items-start justify-between gap-x-4 align-middle">
              <div className="flex w-[50%] flex-col gap-y-4 rounded-lg border-[1px] border-bordergray p-3">
                <h2 className="text-lg font-semibold">General Information</h2>
                <div className="flex h-[240px] max-h-[240px] flex-row gap-6">
                  {/* Truck Image */}
                  {assetImageUrl && assetImageUrl?.length > 0 && (
                    <div className="w-1/2 bg-[#EAECF0] p-3">
                      <img
                        src={assetImageUrl}
                        alt="Truck"
                        className="h-full w-full rounded-lg object-cover"
                      />
                    </div>
                  )}
                  {/* Info Section */}
                  <div
                    className={`grid grid-cols-1 gap-1 ${assetImageUrl && assetImageUrl?.length ? 'w-1/2' : 'w-full'}`}
                  >
                    {/* VIN Number */}
                    <div className="flex flex-row items-center gap-x-2 bg-primarybg px-2 py-[2px] align-middle">
                      <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                        VIN Number
                      </p>
                      <p className="text-[14px] text-primary">
                        {selectedAsset?.vehicle_id_number}
                      </p>
                    </div>
                    {/* Class */}
                    {selectedAsset?.asset_type === 'truck' && (
                      <div className="flex flex-row items-start gap-x-2 px-2 py-[2px] align-middle">
                        <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                          Class
                        </p>
                        <p className="text-[14px] text-primary">
                          Class {selectedAsset?.truck_class_number} <br />
                          {selectedAsset?.truck_class}
                        </p>
                      </div>
                    )}
                    {/* Make */}
                    <div
                      className={`flex flex-row items-center gap-x-2 px-2 py-[2px] align-middle ${selectedAsset?.asset_type === 'truck' && 'bg-primarybg'}`}
                    >
                      <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                        Make
                      </p>
                      <p className="text-[14px] text-primary">
                        {selectedAsset?.asset_type === 'truck'
                          ? capitalize(selectedAsset?.truck_make)
                          : capitalize(selectedAsset?.trailer_make)}
                      </p>
                    </div>
                    {/* Model */}
                    <div
                      className={`flex flex-row items-center gap-x-2 px-2 py-[2px] align-middle ${selectedAsset?.asset_type === 'trailer' && 'bg-primarybg'}`}
                    >
                      <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                        Model
                      </p>
                      <p className="text-[14px] text-primary">
                        {selectedAsset?.asset_type === 'truck'
                          ? capitalize(selectedAsset?.truck_model)
                          : capitalize(selectedAsset?.trailer_model)}
                      </p>
                    </div>
                    {/* Year */}
                    <div
                      className={`flex flex-row items-center gap-x-2 px-2 py-[2px] align-middle ${selectedAsset?.asset_type === 'truck' && 'bg-primarybg'}`}
                    >
                      <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                        Year
                      </p>
                      <p className="text-[14px] text-primary">
                        {selectedAsset?.asset_type === 'truck'
                          ? selectedAsset?.truck_year
                          : selectedAsset?.trailer_year}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex w-[50%] flex-col gap-y-4 rounded-lg border-[1px] border-bordergray p-3">
                <h2 className="text-lg font-semibold">
                  Operational Information
                </h2>
                <div className="flex h-[400px] max-h-[400px] flex-row gap-6">
                  {/* Info Section */}
                  <div className={`grid w-full grid-cols-1 gap-1`}>
                    {/* VIN Number */}
                    <div className="flex flex-row items-center gap-x-2 bg-primarybg px-2 py-[2px] align-middle">
                      <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                        Ownership Status
                      </p>
                      <p className="text-[14px] text-primary">
                        {selectedAsset?.ownership_status === 'leaseToOwn'
                          ? 'Lease to Own'
                          : capitalize(selectedAsset?.ownership_status)}
                      </p>
                    </div>
                    <div className="flex flex-row items-center gap-x-2 px-2 py-[2px] align-middle">
                      <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                        {capitalize(selectedAsset?.asset_type)}{' '}
                        Payment Amount
                      </p>
                      <p className="text-[14px] text-primary">
                        {formatNumberCommasWithDollar(
                          selectedAsset?.asset_type === 'truck'
                            ? selectedAsset?.truck_payment_amount
                            : selectedAsset?.trailer_payment_amount
                        )}{' '}
                        (per month)
                      </p>
                    </div>
                    {/* Make */}
                    <div
                      className={`flex flex-row items-center gap-x-2 bg-primarybg px-2 py-[2px] align-middle`}
                    >
                      <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                        Insurance Payment Amount
                      </p>
                      <p className="text-[14px] text-primary">
                        {isFleetInsurance ? (
                          FLEET_POLICY_LABEL
                        ) : (
                          <>
                            {formatNumberCommasWithDollar(
                              selectedAsset?.insurance_payment_amount
                            )}{' '}
                            (per month)
                          </>
                        )}
                      </p>
                    </div>
                    {/* Model */}
                    {selectedAsset?.asset_type === 'truck' && (
                      <div
                        className={`flex flex-row items-center gap-x-2 px-2 py-[2px] align-middle`}
                      >
                        <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                          Current Mileage
                        </p>
                        <p className="text-[14px] text-primary">
                          {formatNumberCommas(selectedAsset?.current_mileage)}
                        </p>
                      </div>
                    )}
                    {/* Year */}
                    {selectedAsset?.asset_type === 'truck' && (
                      <div
                        className={`flex flex-row items-center gap-x-2 bg-primarybg px-2 py-[2px] align-middle`}
                      >
                        <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                          Typical Annual Mileage on Road
                        </p>
                        <p className="text-[14px] text-primary">
                          {formatNumberCommas(
                            selectedAsset?.typical_annual_mileage
                          )}
                        </p>
                      </div>
                    )}

                    <div
                      className={`flex flex-row items-center gap-x-2 px-2 py-[2px] align-middle`}
                    >
                      <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                        Tire Brand Quality
                      </p>
                      <p className="text-[14px] text-primary">
                        {capitalize(selectedAsset?.tire_brand_quality)}
                      </p>
                    </div>

                    {selectedAsset?.asset_type === 'truck' && (
                      <div
                        className={`flex flex-row items-center gap-x-2 bg-primarybg px-2 py-[2px] align-middle`}
                      >
                        <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                          Status
                        </p>
                        <p className="text-[14px] text-primary">
                          {selectedAsset?.is_currently_out_of_service
                            ? 'Out of Commission'
                            : 'In Commission'}
                        </p>
                      </div>
                    )}

                    {selectedAsset?.asset_type === 'truck' &&
                      selectedAsset?.is_currently_out_of_service && (
                        <div
                          className={`flex flex-row items-center gap-x-2 px-2 py-[2px] align-middle`}
                        >
                          <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                            Reason for Being Out of Comission
                          </p>
                          <p className="text-[14px] text-primary">
                            {capitalize(selectedAsset?.reason?.split('_').join(' '))}
                          </p>
                        </div>
                      )}

                    {selectedAsset?.asset_type === 'truck' && (
                      <div
                        className={`flex flex-row items-center gap-x-2 ${selectedAsset?.is_currently_out_of_service && 'bg-primarybg'} px-2 py-[2px] align-middle`}
                      >
                        <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                          Engine Condition
                        </p>
                        <p className="text-[14px] text-primary">
                          {capitalize(selectedAsset?.engine_condition)}
                        </p>
                      </div>
                    )}

                    <div
                      className={`flex flex-row items-center gap-x-2 px-2 py-[2px] ${!selectedAsset?.is_currently_out_of_service && 'bg-primarybg'} align-middle`}
                    >
                      <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                        Responsibility for Maintenance and Repairs
                      </p>
                      <p className="text-[14px] text-primary">
                        {selectedAsset?.pay_for_repairs ? 'Pays' : 'Do Not Pay'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'tablet':
        return (
          <div
            className={`flex h-full w-full flex-col items-center gap-6 border-[1px] border-bordergray bg-white p-4 align-middle`}
          >
            <div className="flex h-full w-full flex-col items-start justify-between gap-4 align-middle">
              <div className="flex w-full flex-col gap-y-4 rounded-lg border-[1px] border-bordergray p-3">
                <h2 className="text-lg font-semibold">General Information</h2>
                <div className="flex w-full flex-col gap-6">
                  {/* Truck Image */}
                  {assetImageUrl && assetImageUrl?.length > 0 && (
                    <div className="w-full bg-[#EAECF0] p-3">
                      <img
                        src={assetImageUrl}
                        alt="Truck"
                        className="h-full w-full rounded-lg object-cover"
                      />
                    </div>
                  )}
                  {/* Info Section */}
                  <div className={`grid w-full grid-cols-1 gap-1`}>
                    {/* VIN Number */}
                    <div className="flex flex-row items-center gap-x-2 bg-primarybg px-2 py-[2px] align-middle">
                      <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                        VIN Number
                      </p>
                      <p className="text-[14px] text-primary">
                        {selectedAsset?.vehicle_id_number}
                      </p>
                    </div>
                    {/* Class */}
                    {selectedAsset?.asset_type === 'truck' && (
                      <div className="flex flex-row items-start gap-x-2 px-2 py-[2px] align-middle">
                        <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                          Class
                        </p>
                        <p className="text-[14px] text-primary">
                          Class {selectedAsset?.truck_class_number} <br />
                          {selectedAsset?.truck_class}
                        </p>
                      </div>
                    )}
                    {/* Make */}
                    <div
                      className={`flex flex-row items-center gap-x-2 px-2 py-[2px] align-middle ${selectedAsset?.asset_type === 'truck' && 'bg-primarybg'}`}
                    >
                      <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                        Make
                      </p>
                      <p className="text-[14px] text-primary">
                        {selectedAsset?.asset_type === 'truck'
                          ? capitalize(selectedAsset?.truck_make)
                          : capitalize(selectedAsset?.trailer_make)}
                      </p>
                    </div>
                    {/* Model */}
                    <div
                      className={`flex flex-row items-center gap-x-2 px-2 py-[2px] align-middle ${selectedAsset?.asset_type === 'trailer' && 'bg-primarybg'}`}
                    >
                      <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                        Model
                      </p>
                      <p className="text-[14px] text-primary">
                        {selectedAsset?.asset_type === 'truck'
                          ? capitalize(selectedAsset?.truck_model)
                          : capitalize(selectedAsset?.trailer_model)}
                      </p>
                    </div>
                    {/* Year */}
                    <div
                      className={`flex flex-row items-center gap-x-2 px-2 py-[2px] align-middle ${selectedAsset?.asset_type === 'truck' && 'bg-primarybg'}`}
                    >
                      <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                        Year
                      </p>
                      <p className="text-[14px] text-primary">
                        {selectedAsset?.asset_type === 'truck'
                          ? selectedAsset?.truck_year
                          : selectedAsset?.trailer_year}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex w-full flex-col gap-y-4 rounded-lg border-[1px] border-bordergray p-3">
                <h2 className="text-lg font-semibold">
                  Operational Information
                </h2>
                <div className="flex h-[400px] max-h-[400px] flex-row gap-6">
                  {/* Info Section */}
                  <div className={`grid w-full grid-cols-1 gap-1`}>
                    {/* VIN Number */}
                    <div className="flex flex-row items-center gap-x-2 bg-primarybg px-2 py-[2px] align-middle">
                      <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                        Ownership Status
                      </p>
                      <p className="text-[14px] text-primary">
                        {selectedAsset?.ownership_status === 'leaseToOwn'
                          ? 'Lease to Own'
                          : capitalize(selectedAsset?.ownership_status)}
                      </p>
                    </div>
                    <div className="flex flex-row items-center gap-x-2 px-2 py-[2px] align-middle">
                      <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                        {capitalize(selectedAsset?.asset_type)}{' '}
                        Payment Amount
                      </p>
                      <p className="text-[14px] text-primary">
                        {formatNumberCommasWithDollar(
                          selectedAsset?.asset_type === 'truck'
                            ? selectedAsset?.truck_payment_amount
                            : selectedAsset?.trailer_payment_amount
                        )}{' '}
                        (per month)
                      </p>
                    </div>
                    {/* Make */}
                    <div
                      className={`flex flex-row items-center gap-x-2 bg-primarybg px-2 py-[2px] align-middle`}
                    >
                      <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                        Insurance Payment Amount
                      </p>
                      <p className="text-[14px] text-primary">
                        {isFleetInsurance ? (
                          FLEET_POLICY_LABEL
                        ) : (
                          <>
                            {formatNumberCommasWithDollar(
                              selectedAsset?.insurance_payment_amount
                            )}{' '}
                            (per month)
                          </>
                        )}
                      </p>
                    </div>
                    {/* Model */}
                    {selectedAsset?.asset_type === 'truck' && (
                      <div
                        className={`flex flex-row items-center gap-x-2 px-2 py-[2px] align-middle`}
                      >
                        <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                          Current Mileage
                        </p>
                        <p className="text-[14px] text-primary">
                          {formatNumberCommas(selectedAsset?.current_mileage)}
                        </p>
                      </div>
                    )}
                    {/* Year */}
                    {selectedAsset?.asset_type === 'truck' && (
                      <div
                        className={`flex flex-row items-center gap-x-2 bg-primarybg px-2 py-[2px] align-middle`}
                      >
                        <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                          Typical Annual Mileage on Road
                        </p>
                        <p className="text-[14px] text-primary">
                          {formatNumberCommas(
                            selectedAsset?.typical_annual_mileage
                          )}
                        </p>
                      </div>
                    )}

                    <div
                      className={`flex flex-row items-center gap-x-2 px-2 py-[2px] align-middle`}
                    >
                      <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                        Tire Brand Quality
                      </p>
                      <p className="text-[14px] text-primary">
                        {capitalize(selectedAsset?.tire_brand_quality)}
                      </p>
                    </div>

                    {selectedAsset?.asset_type === 'truck' && (
                      <div
                        className={`flex flex-row items-center gap-x-2 bg-primarybg px-2 py-[2px] align-middle`}
                      >
                        <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                          Status
                        </p>
                        <p className="text-[14px] text-primary">
                          {selectedAsset?.is_currently_out_of_service
                            ? 'Out of Commission'
                            : 'In Commission'}
                        </p>
                      </div>
                    )}

                    {selectedAsset?.asset_type === 'truck' &&
                      selectedAsset?.is_currently_out_of_service && (
                        <div
                          className={`flex flex-row items-center gap-x-2 px-2 py-[2px] align-middle`}
                        >
                          <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                            Reason for Being Out of Comission
                          </p>
                          <p className="text-[14px] text-primary">
                            {capitalize(selectedAsset?.reason?.split('_').join(' '))}
                          </p>
                        </div>
                      )}

                    {selectedAsset?.asset_type === 'truck' && (
                      <div
                        className={`flex flex-row items-center gap-x-2 ${selectedAsset?.is_currently_out_of_service && 'bg-primarybg'} px-2 py-[2px] align-middle`}
                      >
                        <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                          Engine Condition
                        </p>
                        <p className="text-[14px] text-primary">
                          {capitalize(selectedAsset?.engine_condition)}
                        </p>
                      </div>
                    )}

                    <div
                      className={`flex flex-row items-center ${!selectedAsset?.is_currently_out_of_service && 'bg-primarybg'} gap-x-2 px-2 py-[2px] align-middle`}
                    >
                      <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                        Responsibility for Maintenance and Repairs
                      </p>
                      <p className="text-[14px] text-primary">
                        {selectedAsset?.pay_for_repairs ? 'Pays' : 'Do Not Pay'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'mobile':
        return (
          <div
            className={`flex h-full w-full flex-col items-center gap-6 border-[1px] border-bordergray bg-white p-4 align-middle`}
          >
            <div className="flex h-full w-full flex-col items-start justify-between gap-4 align-middle">
              <div className="flex w-full flex-col gap-y-4 rounded-lg border-[1px] border-bordergray p-3">
                <h2 className="text-lg font-semibold">General Information</h2>
                <div className="flex h-full w-full flex-col gap-6">
                  {/* Truck Image */}
                  {assetImageUrl && assetImageUrl?.length > 0 && (
                    <div className="w-full bg-[#EAECF0] p-3">
                      <img
                        src={assetImageUrl}
                        alt="Truck"
                        className="h-full w-full rounded-lg object-cover"
                      />
                    </div>
                  )}
                  {/* Info Section */}
                  <div className={`grid w-full grid-cols-1 gap-1`}>
                    {/* VIN Number */}
                    <div className="flex flex-row items-center gap-x-2 bg-primarybg px-2 py-[2px] align-middle">
                      <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                        VIN Number
                      </p>
                      <p className="text-[14px] text-primary">
                        {selectedAsset?.vehicle_id_number}
                      </p>
                    </div>
                    {/* Class */}
                    {selectedAsset?.asset_type === 'truck' && (
                      <div className="flex flex-row items-start gap-x-2 px-2 py-[2px] align-middle">
                        <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                          Class
                        </p>
                        <p className="text-[14px] text-primary">
                          Class {selectedAsset?.truck_class_number} <br />
                          {selectedAsset?.truck_class}
                        </p>
                      </div>
                    )}
                    {/* Make */}
                    <div
                      className={`flex flex-row items-center gap-x-2 px-2 py-[2px] align-middle ${selectedAsset?.asset_type === 'truck' && 'bg-primarybg'}`}
                    >
                      <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                        Make
                      </p>
                      <p className="text-[14px] text-primary">
                        {selectedAsset?.asset_type === 'truck'
                          ? capitalize(selectedAsset?.truck_make)
                          : capitalize(selectedAsset?.trailer_make)}
                      </p>
                    </div>
                    {/* Model */}
                    <div
                      className={`flex flex-row items-center gap-x-2 px-2 py-[2px] align-middle ${selectedAsset?.asset_type === 'trailer' && 'bg-primarybg'}`}
                    >
                      <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                        Model
                      </p>
                      <p className="text-[14px] text-primary">
                        {selectedAsset?.asset_type === 'truck'
                          ? capitalize(selectedAsset?.truck_model)
                          : capitalize(selectedAsset?.trailer_model)}
                      </p>
                    </div>
                    {/* Year */}
                    <div
                      className={`flex flex-row items-center gap-x-2 px-2 py-[2px] align-middle ${selectedAsset?.asset_type === 'truck' && 'bg-primarybg'}`}
                    >
                      <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                        Year
                      </p>
                      <p className="text-[14px] text-primary">
                        {selectedAsset?.asset_type === 'truck'
                          ? selectedAsset?.truck_year
                          : selectedAsset?.trailer_year}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex h-full w-full flex-col gap-y-4 rounded-lg border-[1px] border-bordergray p-3">
                <h2 className="text-lg font-semibold">
                  Operational Information
                </h2>
                <div className="flex h-full flex-row gap-6">
                  {/* Info Section */}
                  <div className={`grid w-full grid-cols-1 gap-1`}>
                    {/* VIN Number */}
                    <div className="flex flex-row items-center gap-x-2 bg-primarybg px-2 py-[2px] align-middle">
                      <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                        Ownership Status
                      </p>
                      <p className="text-[14px] text-primary">
                        {selectedAsset?.ownership_status === 'leaseToOwn'
                          ? 'Lease to Own'
                          : capitalize(selectedAsset?.ownership_status)}
                      </p>
                    </div>
                    <div className="flex flex-row items-center gap-x-2 px-2 py-[2px] align-middle">
                      <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                        {capitalize(selectedAsset?.asset_type)}{' '}
                        Payment Amount
                      </p>
                      <p className="text-[14px] text-primary">
                        {formatNumberCommasWithDollar(
                          selectedAsset?.asset_type === 'truck'
                            ? selectedAsset?.truck_payment_amount
                            : selectedAsset?.trailer_payment_amount
                        )}{' '}
                        (per month)
                      </p>
                    </div>
                    {/* Make */}
                    <div
                      className={`flex flex-row items-center gap-x-2 bg-primarybg px-2 py-[2px] align-middle`}
                    >
                      <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                        Insurance Payment Amount
                      </p>
                      <p className="text-[14px] text-primary">
                        {isFleetInsurance ? (
                          FLEET_POLICY_LABEL
                        ) : (
                          <>
                            {formatNumberCommasWithDollar(
                              selectedAsset?.insurance_payment_amount
                            )}{' '}
                            (per month)
                          </>
                        )}
                      </p>
                    </div>
                    {/* Model */}
                    {selectedAsset?.asset_type === 'truck' && (
                      <div
                        className={`flex flex-row items-center gap-x-2 px-2 py-[2px] align-middle`}
                      >
                        <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                          Current Mileage
                        </p>
                        <p className="text-[14px] text-primary">
                          {formatNumberCommas(selectedAsset?.current_mileage)}
                        </p>
                      </div>
                    )}
                    {/* Year */}
                    {selectedAsset?.asset_type === 'truck' && (
                      <div
                        className={`flex flex-row items-center gap-x-2 bg-primarybg px-2 py-[2px] align-middle`}
                      >
                        <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                          Typical Annual Mileage on Road
                        </p>
                        <p className="text-[14px] text-primary">
                          {formatNumberCommas(
                            selectedAsset?.typical_annual_mileage
                          )}
                        </p>
                      </div>
                    )}

                    <div
                      className={`flex flex-row items-center gap-x-2 px-2 py-[2px] align-middle`}
                    >
                      <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                        Tire Brand Quality
                      </p>
                      <p className="text-[14px] text-primary">
                        {capitalize(selectedAsset?.tire_brand_quality)}
                      </p>
                    </div>

                    {selectedAsset?.asset_type === 'truck' && (
                      <div
                        className={`flex flex-row items-center gap-x-2 bg-primarybg px-2 py-[2px] align-middle`}
                      >
                        <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                          Status
                        </p>
                        <p className="text-[14px] text-primary">
                          {selectedAsset?.is_currently_out_of_service
                            ? 'Out of Commission'
                            : 'In Commission'}
                        </p>
                      </div>
                    )}

                    {selectedAsset?.asset_type === 'truck' &&
                      selectedAsset?.is_currently_out_of_service && (
                        <div
                          className={`flex flex-row items-center gap-x-2 px-2 py-[2px] align-middle`}
                        >
                          <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                            Reason for Being Out of Comission
                          </p>
                          <p className="text-[14px] text-primary">
                            {capitalize(selectedAsset?.reason?.split('_').join(' '))}
                          </p>
                        </div>
                      )}

                    {selectedAsset?.asset_type === 'truck' && (
                      <div
                        className={`flex flex-row items-center gap-x-2 ${selectedAsset?.is_currently_out_of_service && 'bg-primarybg'} px-2 py-[2px] align-middle`}
                      >
                        <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                          Engine Condition
                        </p>
                        <p className="text-[14px] text-primary">
                          {capitalize(selectedAsset?.engine_condition)}
                        </p>
                      </div>
                    )}

                    <div
                      className={`flex flex-row items-center ${!selectedAsset?.is_currently_out_of_service && 'bg-primarybg'} gap-x-2 px-2 py-[2px] align-middle`}
                    >
                      <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                        Responsibility for Maintenance and Repairs
                      </p>
                      <p className="text-[14px] text-primary">
                        {selectedAsset?.pay_for_repairs ? 'Pays' : 'Do Not Pay'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
    }
  };
  return (
    <>
      {selectedAsset &&
        (selectedAsset?.truck_id?.length > 0 ||
          selectedAsset?.trailer_id?.length > 0) &&
        renderScreenView(screenView)}
    </>
  );
}

export default AssetOverview;
